import { IEmailProvider, EmailData, EmailResponse } from '../types';

/**
 * Vercel/Netlify Serverless Functions Provider
 * Requiere: API endpoint en /api/send-email
 * Usa el archivo que ya creamos
 */
export class VercelAPIProvider implements IEmailProvider {
  private apiEndpoint: string;
  private recaptchaSiteKey: string;

  constructor() {
    this.apiEndpoint = '/api/send-email';
    this.recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
  }

  async initialize(): Promise<void> {
    // Cargar reCAPTCHA script
    if (this.recaptchaSiteKey && !(window as any).grecaptcha) {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${this.recaptchaSiteKey}`;
      script.async = true;
      document.body.appendChild(script);
      
      // Esperar a que se cargue
      await new Promise((resolve) => {
        script.onload = resolve;
      });
    }
  }

  private async getRecaptchaToken(): Promise<string> {
    if (!this.recaptchaSiteKey) return '';

    return new Promise((resolve, reject) => {
      (window as any).grecaptcha.ready(() => {
        (window as any).grecaptcha
          .execute(this.recaptchaSiteKey, { action: 'contact_form' })
          .then((token: string) => resolve(token))
          .catch((error: any) => reject(error));
      });
    });
  }

  async sendEmail(data: EmailData): Promise<EmailResponse> {
    try {
      // Si el token ya viene en data (desde Contact.tsx), usarlo
      // Si no, intentar obtenerlo aquí (backward compatibility)
      let recaptchaToken = data.recaptchaToken;
      
      if (!recaptchaToken && this.recaptchaSiteKey) {
        try {
          recaptchaToken = await this.getRecaptchaToken();
        } catch (error) {
          console.warn('⚠️ No se pudo obtener token de reCAPTCHA, continuando sin validación...', error);
        }
      }

      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          recaptchaToken: recaptchaToken || undefined,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send email');
      }

      return {
        success: true,
        message: result.message || 'Email sent successfully via Vercel API',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to send email via Vercel API',
      };
    }
  }
}
